import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KTSVG } from '../../../_metronic/helpers';
import axios from 'axios';
import { SERVER_URL } from '../../ServerConfig';
import { toast } from 'react-toastify';
import { Form, Row, Col } from 'react-bootstrap';
import { PageTitle } from '../../../_metronic/layout/core';
import { createRole, updateRole } from './core/_requests';
import { checkPermission } from 'app/utils/checkPermission';
import { Navigate, Outlet } from 'react-router-dom';
import PermissionThreshold from './PermissionThreshold';

interface PermissionAction {
  id: number;
  action: string;
  status: boolean;
}

interface PermissionGroup {
  name: string;
  actions: PermissionAction[];
}

interface FormData {
  name: string;
  status: number;
}

interface RolePayload {
  name: string;
  status: number;
  permissions: number[];
}

const AddRole: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState<PermissionGroup[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, number[]>>({});
  const [formData, setFormData] = useState<FormData>({
    name: '',
    status: 1
  });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    name: '',
    permissions: '',
  });
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchPermissions();
    if (id) {
      fetchRoleData();
    } else {
      nameInputRef.current?.focus();
    }
  }, [id]);

  const fetchRoleData = async () => {
    if (id) {
      try {
        const response = await axios.get(`${SERVER_URL}/role/${id}`);
        if (response.data.responseStatus === 200 && response.data.responseData) {
          const roleData = response.data.responseData;
          setFormData({
            name: roleData.name,
            status: roleData.status
          });

          const initialSelectedPermissions: Record<string, number[]> = {};
          roleData.permissions.forEach((group: PermissionGroup) => {
            initialSelectedPermissions[group.name] = group.actions
              .filter(action => action.status)
              .map(action => action.id);
          });
          setSelectedPermissions(initialSelectedPermissions);
        }
      } catch (error) {
        console.error('Error fetching role:', error);
      }
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/permission`);
      if (response.data.responseStatus === 200) {
        const formattedPermissions: PermissionGroup[] = response.data.responseData.map((group: any) => ({
          name: group.name,
          actions: group.actions.map((action: any) => ({
            id: action.id,
            action: action.action,
            status: action.status
          }))
        }));
        setPermissions(formattedPermissions);
        if (!id) {
          setSelectedPermissions({});
        }
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handlePermissionChange = (name: string, actionId: number) => {
    setSelectedPermissions(prev => {
      const currentIds = prev[name] || [];
      const isSelected = currentIds.includes(actionId);

      let newIds: number[];
      if (isSelected) {
        newIds = currentIds.filter(id => id !== actionId);
      } else {
        newIds = [...currentIds, actionId];
      }

      const group = permissions.find(g => g.name === name);
      const listAction = group?.actions.find(a => a.action === 'list');
      const selectedAction = group?.actions.find(a => a.id === actionId);

      if (listAction && selectedAction?.action !== 'list' && !isSelected) {
        if (!newIds.includes(listAction.id)) {
          newIds.push(listAction.id);
        }
      }

      return {
        ...prev,
        [name]: newIds
      };
    });
  };

  const handleGroupSelectAll = (name: string) => {
    setSelectedPermissions(prev => {
      const group = permissions.find(g => g.name === name);
      if (!group) return prev;

      const currentIds = prev[name] || [];
      const allActionIds = group.actions.map(a => a.id);
      
      // If all actions are already selected, deselect all
      if (currentIds.length === allActionIds.length) {
        const newPermissions = { ...prev };
        newPermissions[name] = [];
        return newPermissions;
      }
      
      // Otherwise, select all actions
      const newPermissions = { ...prev };
      newPermissions[name] = allActionIds;
      return newPermissions;
    });
  };

  const isGroupAllSelected = (name: string) => {
    const group = permissions.find(g => g.name === name);
    if (!group) return false;

    const selectedIds = selectedPermissions[name] || [];
    const allActionIds = group.actions.map(a => a.id);
    return selectedIds.length === allActionIds.length && allActionIds.every(id => selectedIds.includes(id));
  };

  const handleSelectAllPermissions = () => {
    setSelectedPermissions(prev => {
      const allSelected = permissions.every(group => isGroupAllSelected(group.name));
      const newPermissions: Record<string, number[]> = {};

      if (allSelected) {
        // If all are selected, deselect all
        permissions.forEach(group => {
          newPermissions[group.name] = [];
        });
      } else {
        // If not all are selected, select all
        permissions.forEach(group => {
          newPermissions[group.name] = group.actions.map(a => a.id);
        });
      }

      return newPermissions;
    });
  };

  const handleSectionSelectAll = (groupNames: string[]) => {
    setSelectedPermissions(prev => {
      const allSelected = groupNames.every(name => isGroupAllSelected(name));
      const newPermissions = { ...prev };

      groupNames.forEach(name => {
        const group = permissions.find(g => g.name === name);
        if (!group) return;

        if (allSelected) {
          // If all are selected, deselect all
          newPermissions[name] = [];
        } else {
          // If not all are selected, select all
          newPermissions[name] = group.actions.map(a => a.id);
        }
      });

      return newPermissions;
    });
  };
 if (id == '1') {
    return <Navigate to='/error/403' replace />;
  }
  const isListDisabled = (name: string) => {
    const group = permissions.find(g => g.name === name);
    if (!group) return false;

    const listAction = group.actions.find(a => a.action === 'list');
    const selectedIds = selectedPermissions[name] || [];

    const hasOtherPermissions = group.actions.some(
      action => action.id !== listAction?.id && selectedIds.includes(action.id)
    );

    return hasOtherPermissions;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedFormData = {
      ...formData,
      name: formData.name.trim()
    };
    setFormData(trimmedFormData);

    if (!validateForm()) return;

    setLoading(true);
    try {
      const permissionIds = Object.values(selectedPermissions).flat();
      const payload: RolePayload = {
        name: trimmedFormData.name,
        status: trimmedFormData.status,
        permissions: permissionIds
      };

      if (id) {
        await updateRole(parseInt(id, 10), payload);
        toast.success('Role updated successfully');
      } else {
        await createRole(payload);
        toast.success('Role created successfully');
      }
      navigate('/role-management');
    } catch (error: any) {
      console.error('Error saving role:', error);

      const responseStatus = error?.response?.status;
      const apiMessage = error?.response?.data?.responseMessage;

      if (responseStatus === 400 && apiMessage === 'Role is already exists.') {
        toast.error('Role already exists');
      } else {
        toast.error(apiMessage || error.message || 'Failed to save role');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {
      name: '',
      permissions: ''
    };

    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
    }

    // Check if any permissions are actually selected
    const hasSelectedPermissions = Object.values(selectedPermissions).some(
      permissionIds => permissionIds.length > 0
    );

    if (!hasSelectedPermissions) {
      errors.permissions = 'Please select at least one permission';
    }

    setFormErrors(errors);
    return !errors.name && !errors.permissions;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const navigateToRoleListing = () => {
    navigate('/role-management');
  };

  return (
    <>
      <PageTitle>{id ? 'Edit Role' : 'Add Role'}</PageTitle>
      <Col className='position-absolute me-5 end-0 tp-btn-mn mt-5'>
        <button
          type='button'
          onClick={navigateToRoleListing}
          value=''
          className='btn btn-sm fs-5 btn-light-primary me-5'
        >
          Back
        </button>
      </Col>
      <div className='card card-body p-12 mt-10'>
        <div className='card mb-5 mb-xl-10'>
          <form onSubmit={handleSubmit} className='form'>
            <div className='card-body border-top p-9'>
              <div className='row mb-6'>
                <div className='col-12'>
                  <div className='row mb-6'>
                    <div className='col-md-6'>
                      <label className='col-form-label required fw-bold fs-6'>Role Name</label>
                      <input
                        type='text'
                        className={`form-control form-control-lg form-control-solid mb-3 mb-lg-0 ${formErrors.name ? 'is-invalid' : ''}`}
                        placeholder='Role name'
                        name='name'
                        value={formData.name}
                        onChange={handleInputChange}
                        ref={nameInputRef}
                      />
                      {formErrors.name && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{formErrors.name}</div>
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <label className='col-form-label fw-bold fs-6'>Status</label>
                      <div className='d-flex align-items-center mt-3'>
                        <label className='form-check form-check-inline form-check-solid me-5'>
                          <input
                            className='form-check-input'
                            name='status'
                            type='radio'
                            value='1'
                            checked={formData.status === 1}
                            onChange={handleInputChange}
                          />
                          <span className='fw-bold ps-2 fs-6'>Active</span>
                        </label>

                        <label className='form-check form-check-inline form-check-solid'>
                          <input
                            className='form-check-input'
                            name='status'
                            type='radio'
                            value='0'
                            checked={formData.status === 0}
                            onChange={handleInputChange}
                          />
                          <span className='fw-bold ps-2 fs-6'>Inactive</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='separator separator-dashed my-6'></div>

              <div className='row mb-6'>
                <div className='col-12'>
                  {checkPermission('Roles', 'permission_edit') && (
                    <>
                      <div className='d-flex justify-content-between align-items-center mb-3'>
                        <label className='fw-bold fs-6'>Permissions</label>
                        <div className='form-check form-check-custom form-check-solid'>
                          <Form.Check
                            type='checkbox'
                            id='select-all-permissions'
                            checked={permissions.every(group => isGroupAllSelected(group.name))}
                            onChange={handleSelectAllPermissions}
                            label='Select All Permissions'
                          />
                        </div>
                      </div>

                      <PermissionThreshold
                        permissions={permissions}
                        selectedPermissions={selectedPermissions}
                        handlePermissionChange={handlePermissionChange}
                        isGroupAllSelected={isGroupAllSelected}
                        handleGroupSelectAll={handleGroupSelectAll}
                        handleSectionSelectAll={handleSectionSelectAll}
                        isListDisabled={isListDisabled}
                        formErrors={formErrors}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className='card-footer d-flex justify-content-end py-6 px-9'>
              <button
                type='button'
                className='btn btn-sm fs-5 btn-light-primary me-5'
                onClick={navigateToRoleListing}
              >
                Cancel
              </button>

              <button
                type='submit'
                className='btn btn-sm fs-5 btn-primary'
                disabled={loading}
              >
                {loading ? (
                  <span className='indicator-progress'>
                    Please wait...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  id ? 'Update' : 'Add'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddRole; 